/* ⚠️ GENERATED from app/services/avatarKit.ts by gen-twins.mjs — edit the TypeScript, not this. */
// services/avatarKit.ts — our own avatar system: the spec, the catalogue, the colours.
//
// 🟥 EVERY SELFIE-TO-AVATAR SERVICE LEFT IS $800 A MONTH (MetaPerson, Avaturn)
//    OR GONE (Ready Player Me, Union Avatars). An avatar is a body, a face, hair
//    and clothes; none of that needs a vendor. This kit is the vendor: a
//    catalogue of parts we generate ourselves from the MakeHuman base mesh
//    (CC0) — six bodies (two genders × three faces), each with its own eyes,
//    brows, hair styles, tops, bottoms, shoes and hats cut to fit it, all
//    rigged to ONE Mixamo-named skeleton per body so the motion code that
//    already exists (peek, wave, nod, blink, smile) drives them unchanged.
//
// 🟢 AN AVATAR IS A SPEC, NOT A FILE. What the profile stores is this small
//    JSON — which body, which parts, which colours — and every viewer
//    ASSEMBLES it from catalogue parts it caches once and shares across every
//    avatar that uses them. No per-user model upload, no Storage copy, nothing
//    to go dark when a vendor does. The rendered portrait is still uploaded as
//    the ordinary profile picture, exactly as before.
//
// 🟢 THE SKIN IS ONE TEXTURE, TINTED. The catalogue bakes each gender's skin
//    once at a light neutral tone; a tone is a per-channel multiplier applied
//    at render time (`skinTint`), so twelve tones cost nothing and a selfie's
//    sampled tone can be matched exactly.
//
// 🟢 PURE. No React, no three, no firebase — a twin is generated for the web
//    (tools/gen-twins.mjs) and the tests run this file as is. The assembly in
//    three.js lives in services/avatarAssemble.ts; the phone's loader in
//    src/avatar/kitCache.ts.

import { POSE_IDS as _POSE_IDS, DEFAULT_POSE as _DEFAULT_POSE } from './avatarPose';
import { normalizeFace as _normFace, faceKeyPart as _faceKey } from './avatarFace';
import { normalizeBody as _normBody, bodyKeyPart as _bodyKey } from './avatarBody';
export const KIT_VERSION = 'v1';
/** where the catalogue is served from, under BACKEND_URL */
export const KIT_PATH = '/avatar-kit';
/* 🟢 A POSE COSTS NOTHING TO STORE AND NOTHING TO FETCH. It is a word; the
   solver in services/avatarPose.ts turns it into bone rotations on the
   assembled skeleton. See that file for why it is not baked geometry. */
export { POSE_IDS, DEFAULT_POSE } from './avatarPose';
/* 🟢 the face is twenty-three numbers, read from a selfie or nudged by hand —
   services/avatarFace.ts. An empty object is the catalogue's own face. */
export * from './avatarFace';
/* 🟢 and the build is eleven more — shoulders, waist, arms — on top of the one
   `build` slider, which is still the overall size. services/avatarBody.ts. */
export * from './avatarBody';

/** The whole avatar. Everything a viewer needs to rebuild it, and nothing else. */

/* ─── what the catalogue offers ────────────────────────────────────────── */

export const FACES = ['a', 'b', 'c'];
export const EYE_COLORS = ['brown', 'hazel', 'blue', 'green', 'grey', 'dark'];
export const HAIR_STYLES = ['none', 'buzz', 'short', 'bangs', 'medium', 'bob', 'long', 'curly', 'bun', 'ponytail'];
export const TOP_STYLES = ['tank', 'tee', 'longsleeve', 'sweater', 'jacket'];
export const PANTS_STYLES = ['jeans', 'chino', 'shorts', 'joggers'];
export const SKIRT_STYLES = ['skirt-short', 'skirt-long'];
export const BOTTOM_STYLES = [...PANTS_STYLES, ...SKIRT_STYLES];
export const SHOE_STYLES = ['sneakers', 'boots', 'slides'];
export const HAT_STYLES = ['none', 'cap', 'beanie', 'bucket'];

/** Which part kind a bottom style is. */
export function bottomKind(style) {
  return style.startsWith('skirt') ? 'skirt' : 'pants';
}

/* ─── colours ──────────────────────────────────────────────────────────── */

/** The catalogue's skin was baked at this sRGB tone; every tone is ≤ it per channel. */
export const BAKE_TONE = '#F0DDCF';

/** Twelve tones, light to deep — Fitzpatrick-spanning, none lighter than the bake. */
export const SKIN_TONES = ['#F0DDCF', '#EBCDB6', '#E3BC9C', '#D9A985', '#CB9469', '#B98056', '#A56B45', '#8E5837', '#75452B', '#5E3622', '#472A1B', '#2F1C12'];
export const HAIR_COLORS = ['#1A1210', '#2E1E14', '#4A2F1E', '#6A4630', '#8C5A3B', '#B07A4A', '#D9A866', '#E8CC8A', '#B8B2AD', '#EDE6DD', '#8A2A1E', '#C7412E'];
export const CLOTH_COLORS = ['#F4F4F4', '#C9CCD1', '#8A8F98', '#3E434B', '#1D2026', '#D9463B', '#F2803D', '#F1C232', '#5EAA4C', '#2E8B6A', '#2F6FD6', '#1E3A8A', '#7A4BC7', '#D95A9A', '#8C6A45', '#3B2A20'];
const hexToRgb = hex => {
  const h = String(hex || '').replace('#', '');
  const v = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const n = parseInt(v.slice(0, 6), 16);
  if (!Number.isFinite(n) || v.length < 6) return [0, 0, 0];
  // arithmetic, not shifts: the web twin is linted with no-mixed-operators
  const r = Math.floor(n / 65536) % 256;
  const g = Math.floor(n / 256) % 256;
  const b = n % 256;
  return [r, g, b];
};
export const rgbToHex = (r, g, b) => '#' + [r, g, b].map(x => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, '0')).join('').toUpperCase();

/** sRGB 0..255 → linear 0..1 */
export function srgbToLinear(c) {
  const x = c / 255;
  return x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
}

/**
 * The per-channel multiplier (linear, 0..1) that turns the baked skin into
 * `tone`. Clamped at 1: a tone lighter than the bake is drawn at the bake.
 */
export function skinTint(tone) {
  const t = hexToRgb(tone),
    b = hexToRgb(BAKE_TONE);
  const out = [0, 0, 0];
  for (let i = 0; i < 3; i++) {
    const lb = srgbToLinear(b[i]);
    out[i] = lb > 0 ? Math.min(1, srgbToLinear(t[i]) / lb) : 1;
  }
  return out;
}

/** Nearest swatch to a colour, by linear RGB distance. */
export function nearestSwatch(hex, swatches) {
  const [r, g, b] = hexToRgb(hex).map(srgbToLinear);
  let best = swatches[0],
    bd = Infinity;
  for (const s of swatches) {
    const [sr, sg, sb] = hexToRgb(s).map(srgbToLinear);
    const d = (sr - r) ** 2 + (sg - g) ** 2 + (sb - b) ** 2;
    if (d < bd) {
      bd = d;
      best = s;
    }
  }
  return best;
}

/* ─── the selfie → a skin tone ─────────────────────────────────────────── */

/**
 * The skin tone in a face photo: the median of skin-coloured pixels inside the
 * central ellipse (where the oval asked the face to be). Pixel data is RGBA,
 * row-major. Returns null when too few pixels look like skin (no face there).
 *
 * 🟢 ON THE DEVICE, FROM A THUMBNAIL. A 64×64 downscale is plenty for a median
 *    and it never leaves the phone — the photo is used for this one number.
 */
export function skinToneFromPixels(rgba, width, height) {
  const cx = width / 2,
    cy = height * 0.52,
    rx = width * 0.26,
    ry = height * 0.34;
  const rs = [],
    gs = [],
    bs = [];
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const dx = (x + 0.5 - cx) / rx,
        dy = (y + 0.5 - cy) / ry;
      if (dx * dx + dy * dy > 1) continue;
      const i = (y * width + x) * 4;
      const r = rgba[i],
        g = rgba[i + 1],
        b = rgba[i + 2];
      // YCbCr skin window (Chai & Ngan), widened a little for phone cameras
      const cb = 128 - 0.168736 * r - 0.331264 * g + 0.5 * b;
      const cr = 128 + 0.5 * r - 0.418688 * g - 0.081312 * b;
      const yy = 0.299 * r + 0.587 * g + 0.114 * b;
      if (cb < 77 || cb > 130 || cr < 133 || cr > 177 || yy < 40) continue;
      rs.push(r);
      gs.push(g);
      bs.push(b);
    }
  }
  if (rs.length < 24) return null;
  const med = a => {
    const s = a.slice().sort((p, q) => p - q);
    return s[s.length >> 1];
  };
  return rgbToHex(med(rs), med(gs), med(bs));
}

/* ─── the spec ─────────────────────────────────────────────────────────── */

export function defaultSpec(gender, face = 'a') {
  const f = gender === 'f';
  return {
    v: 1,
    gender,
    face,
    build: 0,
    height: 0,
    skin: SKIN_TONES[2],
    eyes: 'brown',
    hair: {
      style: f ? 'medium' : 'short',
      color: HAIR_COLORS[2]
    },
    top: {
      style: 'tee',
      color: f ? CLOTH_COLORS[13] : CLOTH_COLORS[10]
    },
    bottom: {
      style: 'jeans',
      color: CLOTH_COLORS[11]
    },
    shoes: {
      style: 'sneakers',
      color: CLOTH_COLORS[0]
    },
    hat: null,
    pose: _DEFAULT_POSE,
    face3d: {},
    body3d: {}
  };
}
const clamp = (v, lo, hi, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(lo, Math.min(hi, n)) : d;
};
const isHex = s => typeof s === 'string' && /^#[0-9a-fA-F]{6}$/.test(s);
const part = (p, styles, fallback, allowNone = false) => {
  const st = typeof p?.style === 'string' ? p.style : '';
  const known = styles.includes(st);
  const none = allowNone && st === 'none';
  const style = st && (known || none) ? st : fallback.style;
  const color = isHex(p?.color) ? p.color.toUpperCase() : fallback.color;
  return {
    style,
    color
  };
};

/** A spec from anything (a stored document, a form): every field checked, defaults for the rest. */
export function normalizeSpec(raw) {
  const gender = raw?.gender === 'm' ? 'm' : 'f';
  const face = FACES.includes(raw?.face) ? raw.face : 'a';
  const d = defaultSpec(gender, face);
  const hat = raw?.hat && typeof raw.hat === 'object' && raw.hat.style && raw.hat.style !== 'none' ? part(raw.hat, HAT_STYLES, {
    style: 'cap',
    color: CLOTH_COLORS[3]
  }) : null;
  const out = {
    v: 1,
    gender,
    face,
    build: clamp(raw?.build, -1, 1),
    height: clamp(raw?.height, -1, 1),
    skin: isHex(raw?.skin) ? raw.skin.toUpperCase() : d.skin,
    eyes: EYE_COLORS.includes(raw?.eyes) ? raw.eyes : d.eyes,
    hair: part(raw?.hair, HAIR_STYLES, d.hair),
    top: part(raw?.top, TOP_STYLES, d.top),
    bottom: part(raw?.bottom, BOTTOM_STYLES, d.bottom),
    shoes: part(raw?.shoes, SHOE_STYLES, d.shoes),
    hat,
    pose: _POSE_IDS.includes(raw?.pose) ? raw.pose : d.pose,
    face3d: _normFace(raw?.face3d),
    body3d: _normBody(raw?.body3d)
  };
  /* 🟥 NEVER `undefined` IN THE DOCUMENT. Firestore rejects a field whose value is
     undefined ("Unsupported field value: undefined … avatar.spec.brows") and the
     whole save fails. An optional field is present or absent, never undefined. */
  if (isHex(raw?.brows)) out.brows = raw.brows.toUpperCase();
  return out;
}
export function isValidSpec(raw) {
  return !!raw && typeof raw === 'object' && raw.v === 1 && (raw.gender === 'f' || raw.gender === 'm') && FACES.includes(raw.face) && !!raw.top && !!raw.bottom && !!raw.shoes && !!raw.hair && isHex(raw.skin);
}

/** A stable short id for a spec — the same look is the same key everywhere (caches, the doc id). */
export function specKey(spec) {
  const s = normalizeSpec(spec);
  const str = JSON.stringify([s.gender, s.face, +s.build.toFixed(2), +s.height.toFixed(2), s.skin, s.eyes, s.hair, s.brows || '', s.top, s.bottom, s.shoes, s.hat, s.pose, _faceKey(s.face3d), _bodyKey(s.body3d)]);
  // FNV-1a 32-bit, twice with different seeds → 16 hex chars
  let h1 = 0x811c9dc5,
    h2 = 0x01000193 ^ 0x9e3779b9;
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = Math.imul(h2 ^ c, 0x01000193) >>> 0;
  }
  return h1.toString(16).padStart(8, '0') + h2.toString(16).padStart(8, '0');
}

/* ─── spec → parts ─────────────────────────────────────────────────────── */

/** The catalogue ids a spec needs, in draw order (body first). */
export function partIdsFor(spec) {
  const s = normalizeSpec(spec);
  const v = `${s.gender}${s.face}`;
  const ids = [`body-${v}`, `eyes-${s.eyes}-${v}`, `brows-${v}`];
  if (s.hair.style !== 'none') ids.push(`hair-${s.hair.style}-${v}`);
  ids.push(`top-${s.top.style}-${v}`);
  ids.push(bottomKind(s.bottom.style) === 'skirt' ? `${s.bottom.style}-${v}` : `pants-${s.bottom.style}-${v}`);
  ids.push(`shoes-${s.shoes.style}-${v}`);
  if (s.hat && s.hat.style !== 'none') ids.push(`hat-${s.hat.style}-${v}`);
  return ids;
}

/** The catalogue entries for a spec; throws naming the first part the catalogue lacks. */
export function resolveParts(catalog, spec) {
  const byId = new Map();
  for (const p of catalog?.parts || []) byId.set(p.id, p);
  return partIdsFor(spec).map(id => {
    const p = byId.get(id);
    if (!p) throw new Error(`avatar-kit: no part ${id} in catalogue ${catalog?.version || '?'}`);
    return p;
  });
}

/** The styles the catalogue actually has for a kind, for one body — what the picker lists. */
export function stylesAvailable(catalog, kind, gender, face) {
  const out = [];
  for (const p of catalog?.parts || []) {
    if (p.kind === kind && p.gender === gender && p.face === face && p.style && !out.includes(p.style)) out.push(p.style);
  }
  return out;
}
export function catalogUrl(base) {
  return `${String(base).replace(/\/+$/, '')}${KIT_PATH}/${KIT_VERSION}/catalog.json`;
}
export function partUrl(base, part) {
  return `${String(base).replace(/\/+$/, '')}${KIT_PATH}/${KIT_VERSION}/${part.file}`;
}

/** Bytes a spec downloads the first time (parts are cached and shared after). */
export function specBytes(catalog, spec) {
  try {
    return resolveParts(catalog, spec).reduce((a, p) => a + (p.bytes || 0), 0);
  } catch {
    return 0;
  }
}

/* ─── what a part gets painted ────────────────────────────────────────── */

/** How a part of `kind` should look under this spec. Pure; the assembler applies it. */
export function paintFor(kind, spec) {
  const s = normalizeSpec(spec);
  const morphs = {
    slim: Math.max(0, -s.build),
    heavy: Math.max(0, s.build)
  };
  switch (kind) {
    case 'body':
      return {
        tint: skinTint(s.skin),
        morphs
      };
    case 'hair':
      return {
        color: s.hair.color,
        morphs
      };
    case 'brows':
      return {
        color: s.brows || s.hair.color,
        morphs
      };
    case 'top':
      return {
        color: s.top.color,
        morphs
      };
    case 'pants':
    case 'skirt':
      return {
        color: s.bottom.color,
        morphs
      };
    case 'shoes':
      return {
        color: s.shoes.color,
        morphs
      };
    case 'hat':
      return {
        color: s.hat?.color || CLOTH_COLORS[3],
        morphs
      };
    default:
      return {
        morphs
      };
  }
}

/** The root scale for a height setting. */
export function heightScale(height) {
  return 1 + 0.08 * Math.max(-1, Math.min(1, Number(height) || 0));
}
